import { StandardEvents } from '@shopify/events';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Sticky add-to-cart bar. Visibility: shown while Horizon's buy buttons (or the product form)
 * are above the viewport, hidden while they or the footer are visible. Add: clicks Horizon's
 * real add-to-cart button with data-puppet (skips its fly-to-cart animation), so the request,
 * events and cart drawer are Horizon's. Falls back to tk-cart-api without a product form.
 * Shared modules come from the versioned URLs in data-tk-sync-url / data-tk-cart-url.
 */
class TkStickyAtc extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {IntersectionObserver[]} */
  #observers = [];
  #targetVisible = true;
  #footerVisible = false;
  /** @type {any} */
  #cart;

  connectedCallback() {
    this.#abort = new AbortController();
    this.#observers = [];
    this.#targetVisible = true;
    this.#footerVisible = false;
    const { signal } = this.#abort;
    this.button?.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.#updateButton();

    Promise.all([import(this.dataset.tkSyncUrl ?? ''), import(this.dataset.tkCartUrl ?? '')])
      .then(([sync, cart]) => {
        if (signal.aborted) return;
        this.#cart = cart;
        sync.onVariantChange(
          this,
          (/** @type {any} */ { html, variant }) => {
            sync.syncParts(this, html);
            if (variant === null) this.dataset.variantAvailable = 'false';
            else if (variant?.id) {
              this.dataset.variantId = String(variant.id);
              this.dataset.variantAvailable = String(variant.available !== false);
            }
            this.#updateButton();
          },
          signal
        );
        this.#observe();
      })
      .catch((error) => console.warn('[tk-sticky-atc]', error));
  }

  disconnectedCallback() {
    this.#abort?.abort();
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
    document.body.classList.remove('tk-sticky-active');
    document.documentElement.style.removeProperty('--tk-sticky-h');
  }

  get bar() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.tk-sticky__bar'));
  }

  get button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-tk-sticky-button]'));
  }

  /** @returns {HTMLElement | null} */
  get productForm() {
    return this.#cart ? this.#cart.findProductForm(this, this.dataset.productId) : null;
  }

  /** The element whose visibility decides when the bar shows. */
  get #target() {
    const form = this.productForm;
    return form?.closest('.buy-buttons-block') ?? form ?? document.querySelector('main .shopify-section');
  }

  #observe() {
    const target = this.#target;
    if (target) {
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        // Visible, or not reached yet (below the viewport) => keep the bar hidden.
        this.#targetVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
        this.#render();
      });
      observer.observe(target);
      this.#observers.push(observer);
    }

    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          this.#footerVisible = Boolean(entry?.isIntersecting);
          this.#render();
        },
        { rootMargin: '0px 0px 100px 0px' }
      );
      observer.observe(footer);
      this.#observers.push(observer);
    }
  }

  #render() {
    const stuck = !this.#targetVisible && !this.#footerVisible;
    const bar = this.bar;
    if (!bar) return;
    bar.dataset.stuck = String(stuck);
    bar.toggleAttribute('inert', !stuck);
    document.body.classList.toggle('tk-sticky-active', stuck);
    if (stuck) document.documentElement.style.setProperty('--tk-sticky-h', `${bar.offsetHeight}px`);
  }

  #updateButton() {
    const button = this.button;
    if (!button) return;
    if (this.dataset.action === 'scroll') {
      button.disabled = false;
      return;
    }
    const available = this.dataset.variantAvailable === 'true';
    button.disabled = !available;
    const label = button.querySelector('[data-tk-sync="label"]');
    if (!available && label && this.dataset.unavailableLabel) label.textContent = this.dataset.unavailableLabel;
  }

  #onClick = async () => {
    const button = this.button;
    if (!button || button.disabled) return;

    const form = this.productForm;
    if (this.dataset.action === 'scroll') {
      const picker = form?.closest('.shopify-section')?.querySelector('variant-picker') ?? form;
      picker?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      picker?.querySelector('input, select')?.focus({ preventScroll: true });
      return;
    }

    /** @type {HTMLButtonElement | null} */
    const target = form?.querySelector('[ref="addToCartButton"]') ?? null;
    if (target && !target.disabled) {
      target.dataset.puppet = 'true';
      target.click();
      return;
    }

    const variantId =
      /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value ||
      this.dataset.variantId;
    if (!variantId || !this.#cart) return;
    button.disabled = true;
    const result = await this.#cart.addLines(this, [{ id: variantId, quantity: 1 }]);
    button.disabled = false;
    const live = this.querySelector('[data-live]');
    if (live && !result.ok) live.textContent = result.message ?? '';
  };

  /** Resets Horizon's puppet flag once the add request has settled. */
  #onCartUpdate = (/** @type {any} */ event) => {
    const target = this.productForm?.querySelector('[ref="addToCartButton"]');
    if (!(target instanceof HTMLElement) || target.dataset.puppet !== 'true') return;
    const reset = () => {
      target.dataset.puppet = 'false';
    };
    if (event.promise instanceof Promise) event.promise.finally(reset).catch(() => {});
    else reset();
  };
}

if (!customElements.get('tk-sticky-atc')) customElements.define('tk-sticky-atc', TkStickyAtc);
